import { ChangeDetectionStrategy, Component, inject, input, model, OnInit, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { Select } from 'primeng/select';
import { Convenio, TipoConvenio } from '../../banco-conta-convenio';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-convenio-detalhe',
    standalone: true,
    imports: [DialogoPadraoDirective, CommonModule, ReactiveFormsModule, ButtonModule, Dialog, InputTextModule, MessageModule, ToggleSwitch, Select, CanDirective],
    templateUrl: './convenio-detalhe.html',
    styleUrl: './convenio-detalhe.scss'
})
/** Modal de novo/edição de convênio bancário. */
export class ConvenioDetalhe implements OnInit {
    protected readonly roleBancosEditar = PERMISSOES.configuracoes.bancos.editar;

    private readonly fb = inject(FormBuilder);

    convenio = input<Convenio | null>(null);
    tiposConvenio = input<TipoConvenio[]>([]);
    showDialog = model.required<boolean>();
    salvo = output<Convenio>();

    convenioForm!: FormGroup<{
        convenio: FormControl<string>;
        tipoGeracao: FormControl<string>;
        numeroCarteira: FormControl<string>;
        variacao: FormControl<string>;
        codigoCedente: FormControl<string>;
        compartilhado: FormControl<boolean>;
        tipoConvenio: FormControl<number | null>;
        tipoConvenioRemessa: FormControl<string>;
        status: FormControl<string>;
        registrado: FormControl<boolean>;
        permanenciaBoleto: FormControl<string>;
        apelido: FormControl<string>;
        codigoCedenteCfm: FormControl<string>;
        numeroAgenciaCfm: FormControl<string>;
        percentualRateioCfm: FormControl<number>;
    }>;
    formSubmitted = false;

    ngOnInit(): void {
        this.convenioForm = this.fb.nonNullable.group({
            convenio: ['', [Validators.required]],
            tipoGeracao: ['', [Validators.required]],
            numeroCarteira: ['', [Validators.required]],
            variacao: [''],
            codigoCedente: ['', [Validators.required]],
            compartilhado: [false],
            tipoConvenio: new FormControl<number | null>(null, { validators: [Validators.required] }),
            tipoConvenioRemessa: ['', [Validators.required]],
            status: ['A', [Validators.required]],
            registrado: [false],
            permanenciaBoleto: [''],
            apelido: [''],
            codigoCedenteCfm: [''],
            numeroAgenciaCfm: [''],
            percentualRateioCfm: [0]
        });
    }

    onDialogShow(): void {
        this.formSubmitted = false;
        const c = this.convenio();
        if (c) {
            this.convenioForm.patchValue({
                convenio: c.convenio,
                tipoGeracao: c.tipoGeracao,
                numeroCarteira: c.numeroCarteira,
                variacao: c.variacao,
                codigoCedente: c.codigoCedente,
                compartilhado: c.compartilhado,
                tipoConvenio: c.coTipoConvenio ?? null,
                tipoConvenioRemessa: c.tipoConvenioRemessa,
                status: c.status,
                registrado: c.registrado,
                permanenciaBoleto: c.permanenciaBoleto,
                apelido: c.apelido,
                codigoCedenteCfm: c.codigoCedenteCfm,
                numeroAgenciaCfm: c.numeroAgenciaCfm,
                percentualRateioCfm: c.percentualRateioCfm
            });
        } else {
            this.convenioForm.reset({ compartilhado: false, registrado: false, status: 'A', percentualRateioCfm: 0 });
        }
    }

    hideDialog(): void {
        this.showDialog.set(false);
    }

    isInvalid(controlName: string): boolean {
        const control = this.convenioForm.get(controlName);
        return (control?.invalid && (control?.dirty || control?.touched || this.formSubmitted)) || false;
    }

    salvar(): void {
        this.formSubmitted = true;
        if (this.convenioForm.invalid) return;

        const v = this.convenioForm.getRawValue();
        const tipoSelecionado = this.tiposConvenio().find((tc) => tc.coTipoConvenio === v.tipoConvenio);

        const convenioSalvo: Convenio = {
            ...(this.convenio() || { id: '', parentId: '', usuario: '' }),
            convenio: v.convenio,
            tipoGeracao: v.tipoGeracao,
            numeroCarteira: v.numeroCarteira,
            variacao: v.variacao,
            codigoCedente: v.codigoCedente,
            compartilhado: v.compartilhado,
            tipoConvenio: tipoSelecionado?.sigla ?? '',
            coTipoConvenio: v.tipoConvenio,
            tipoConvenioRemessa: v.tipoConvenioRemessa,
            status: v.status,
            registrado: v.registrado,
            permanenciaBoleto: v.permanenciaBoleto,
            apelido: v.apelido,
            codigoCedenteCfm: v.codigoCedenteCfm,
            numeroAgenciaCfm: v.numeroAgenciaCfm,
            percentualRateioCfm: Number(v.percentualRateioCfm) || 0
        };

        this.salvo.emit(convenioSalvo);
        this.hideDialog();
    }
}
